import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "island_cart_v1";

const VAT_RATE = 0.10;
const LOCAL_DELIVERY_FEE = 5.0;

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], delivery: "collection", familyIsland: "", address: "" };
    const parsed = JSON.parse(raw);
    const validDelivery = ["collection", "local_delivery", "family_island"].includes(parsed.delivery)
      ? parsed.delivery : "collection";
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      delivery: validDelivery,
      familyIsland: parsed.familyIsland || "",
      address: parsed.address || "",
    };
  } catch { return { items: [], delivery: "collection", familyIsland: "", address: "" }; }
}

export function CartProvider({ children }) {
  const initial = loadInitial();
  const [items, setItems] = useState(initial.items);
  const [delivery, setDelivery] = useState(initial.delivery); // "collection" | "local_delivery" | "family_island"
  const [open, setOpen] = useState(false);
  const [wsRules, setWsRules] = useState({
    min_qty_per_design: 5,
    opening_order_min: 150,
    opening_min_first_only: true,
  });
  const { user } = useAuth();

  // A wholesale-approved shopper sees & pays wholesale prices sitewide.
  const isWholesale = user?.role === "wholesale" && user?.status === "approved";
  const minQty = isWholesale ? wsRules.min_qty_per_design : 1;

  // Fetch wholesale rules once — they're small and public.
  useEffect(() => {
    api.get("/wholesale/settings").then(r => setWsRules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, delivery }));
  }, [items, delivery]);

  const addItem = useCallback((product, qty) => {
    const effectiveQty = qty ?? (isWholesale ? wsRules.min_qty_per_design : 1);
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.slug === product.slug);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + effectiveQty };
        return next;
      }
      return [
        ...prev,
        {
          slug: product.slug,
          title: product.title,
          price: product.price,
          wholesale_price: product.wholesale_price,
          image: product.images?.[0] || product.image,
          qty: effectiveQty,
        },
      ];
    });
    setOpen(true);
  }, [isWholesale, wsRules.min_qty_per_design]);

  const removeItem = useCallback((slug) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const updateQty = useCallback((slug, qty) => {
    setItems((prev) =>
      prev.map((i) => (i.slug === slug ? { ...i, qty: Math.max(1, qty) } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const priceOf = useCallback(
    (item) => (isWholesale && item.wholesale_price ? item.wholesale_price : item.price),
    [isWholesale]
  );

  const totals = useMemo(() => {
    const products_subtotal = items.reduce((s, i) => s + priceOf(i) * i.qty, 0);
    const delivery_fee = delivery === "local_delivery" ? LOCAL_DELIVERY_FEE : 0;
    const pre_vat_subtotal = products_subtotal + delivery_fee;
    const vat = pre_vat_subtotal * VAT_RATE;
    const total = pre_vat_subtotal + vat;
    return {
      products_subtotal: +products_subtotal.toFixed(2),
      subtotal: +products_subtotal.toFixed(2),
      delivery_fee,
      pre_vat_subtotal: +pre_vat_subtotal.toFixed(2),
      vat: +vat.toFixed(2),
      total: +total.toFixed(2),
      count: items.reduce((s, i) => s + i.qty, 0),
    };
  }, [items, delivery, priceOf]);

  // Wholesale purchasing rules — surfaced sitewide so retailers see them at
  // every step (add-to-cart, cart sidebar, checkout).
  const wholesaleValidation = useMemo(() => {
    if (!isWholesale) return { valid: true, issues: [] };
    const issues = [];
    const min = wsRules.min_qty_per_design;
    const under = items.filter(i => i.qty < min);
    if (under.length) {
      issues.push({
        key: "min_qty",
        message: `Minimum ${min} cards per design. Update: ${under.map(u => u.title).join(", ")}.`,
      });
    }
    // Opening order minimum applies to first order (or every order if setting off).
    if (totals.products_subtotal < wsRules.opening_order_min) {
      issues.push({
        key: "opening_min",
        message: `Opening wholesale orders must total at least $${wsRules.opening_order_min.toFixed(2)} before VAT and shipping (currently $${totals.products_subtotal.toFixed(2)}).`,
      });
    }
    return { valid: issues.length === 0, issues, minQty: min, openingMin: wsRules.opening_order_min };
  }, [isWholesale, items, totals.products_subtotal, wsRules]);

  return (
    <CartContext.Provider
      value={{
        items, delivery, setDelivery,
        addItem, removeItem, updateQty, clear,
        totals, priceOf, isWholesale, minQty,
        wsRules, wholesaleValidation,
        open, setOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
